import React from 'react';
import {useDispatch} from 'react-redux';
import {App, type URLOpenListenerEvent, type AppState} from '@capacitor/app';
import {SplashScreen} from '@capacitor/splash-screen';

import * as FeedActions from 'web-app/react/feed/actions';
import * as SavedAccountsService from 'web-app/services/selected-save-account';
import * as SessionHelpers from 'web-app/react/session/helpers';
import * as AutoLogoutHelpers from 'web-app/react/session/auto-logout/helpers';
import {customUrlScheme} from 'web-app/util/location';
import PlatformHelper from 'web-app/helpers/platform-helper';
import {openWindow} from 'web-app/react/components/link/helpers';
import {shouldShowImageGallery} from 'web-app/react/image-gallery/helpers';
import * as FileUploadHelpers from 'web-app/react/components/file-upload/helpers';
import store from 'web-app/react/redux/store';
import {StorageHelperPlugin} from 'web-app/custom-capacitor-plugins/storage-plugin';
import {isAccessTokenExpired} from 'web-app/util/access-token-helpers';
import * as SessionSelectors from 'web-app/react/session/selectors';
import {skipHistoryBack} from 'web-app/react/modals-container/signin-modal/helpers';
import {generateHash} from 'web-app/react/routes/route-config';
import {findPathFromUrl} from 'web-app/react/routes/helpers';
import {extractPathFromHash} from 'web-app/util/url';
import {getWhitelabel} from 'web-app/shared/white-labeling-helpers';
import ApiHelper from 'web-app/services/api-helper';

import {extractRouteFromDeepLinkUrl, isWebUrl, getDesiredTLD, removeExtraLeadingSlashes} from './capacitor-helpers';

declare const cordova: any;

export const useMobileAppAccountLifecycle = () => {
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (!PlatformHelper.isMobileApp()) {
            return;
        }

        if (AutoLogoutHelpers.shouldPerformClientSideLogout()) {
            SavedAccountsService.setLoggedInAccountAsSelected();
            SessionHelpers.logOut('', undefined, true);
        }

        App.addListener('appStateChange', (appState: AppState) => {
            if (!appState.isActive) {
                return;
            }

            if (AutoLogoutHelpers.shouldPerformClientSideLogout()) {
                SessionHelpers.logOut('', undefined, true);
            } else {
                dispatch(FeedActions.fetch.action({reload: true}));
            }
        });
    }, [dispatch]);
};

export const useSetupMobileApp = () => {
    React.useEffect(() => {
        if (!PlatformHelper.isMobileApp()) {
            return;
        }

        SplashScreen.hide();

        // This enables us to do deep-linking, see more here: https://capacitorjs.com/docs/apis/app#addlistenerappurlopen
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            const isLoggedIn = SessionSelectors.isLoggedIn(store.getState());
            const customSchemeProtocol = `${customUrlScheme}:`;

            // If it's a web-url, we'll want to select the appropriate TLD before proceeding.
            // Otherwise, we'll use the app's current TLD, which might not match the web-url.
            //
            // This is important, as the app has the ability to target either AWS or the German servers (i.e. app.famly.co or app.famly.co),
            // and we default to the German servers.
            //
            // Normally, we only select the TLD when the user logs in, but in this case we need to do it before, as the
            // user might not be logged in yet, and other routes (e.g. the #/welcome route) would not work without the correct TLD.
            if (isWebUrl(event.url)) {
                const whitelabel = getWhitelabel();
                const tld = getDesiredTLD(whitelabel, event.url);

                if (tld && ApiHelper.apiKeys.includes(`.${tld}`)) {
                    ApiHelper.setApiConfig(`.${tld}`);
                }
            }

            // In some cases, the extracted url from extractRouteFromDeepLinkUrl will have multiple leading slashes between the the hash # and the actual route.
            // Just to be sure, we clean it up here afterwards, so it will correctly matched by findPathFromUrl
            const extractedRoute = removeExtraLeadingSlashes(
                extractRouteFromDeepLinkUrl(event.url, customSchemeProtocol),
            );

            if (!extractedRoute) {
                return;
            }

            const validatedRoute = findPathFromUrl(extractPathFromHash(extractedRoute));

            if (!validatedRoute) {
                window.location.hash = generateHash('/notFound');
                return;
            }

            const isAccountRoute = extractedRoute.startsWith('#/account');

            if (!isAccountRoute || (isLoggedIn && !isAccessTokenExpired())) {
                window.location.hash = extractedRoute;
            } else {
                const urlOrigin = window.location.origin;
                const encodedRoute = encodeURIComponent(`${urlOrigin}/${extractedRoute}`);
                const loginRoute = `#/login?returnUrl=${encodedRoute}`;
                window.location.hash = loginRoute;
            }
        });

        // Handle the "Back" button on Android, see more here: https://capacitorjs.com/docs/apis/app#addlistenerbackbutton
        App.addListener('backButton', () => {
            const state = store.getState();
            if (shouldShowImageGallery(state) || skipHistoryBack(state)) {
                return;
            }
            window.history.back();
        });

        /* We clear the cache also on app startup to ensure
         * that no pictures taken in the app are left behind,
         * e.g. due to the app crash.
         */
        if (PlatformHelper.isIos()) {
            StorageHelperPlugin.cleanTmpFolder();
        } else if (PlatformHelper.isAndroid()) {
            FileUploadHelpers.cleanDocumentScannerFiles();
        }

        if (PlatformHelper.isIos()) {
            document.querySelector('body')?.addEventListener('click', function (event) {
                const target = event.target as HTMLElement;
                if (target?.classList.contains('external')) {
                    event.preventDefault();
                    const href = target.getAttribute('href');
                    if (href) {
                        cordova.InAppBrowser.open(href, '_system', 'location=no');
                    }
                }
            });
        } else if (PlatformHelper.isAndroid()) {
            document.querySelector('body')?.addEventListener('click', function (event) {
                const target = event.target as HTMLElement;
                if (target?.classList.contains('file') || target?.classList.contains('external')) {
                    event.preventDefault();
                    const href = target.getAttribute('href');
                    if (href) {
                        openWindow(href, '_system', 'location=no');
                    }
                }
            });
        }
    }, []);
};
